import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronRight, Play, Star, Users, CheckCircle, Shield, Zap, Gift, BadgeCheck, Sparkles, Lock, Heart, ThumbsUp } from 'lucide-react';
import { trackButtonClick } from '../../utils/analytics';
import { signalPricingCtaArrival } from '../../utils/ctaActions';
import { PromoBanner } from './PromoBanner';

// Available icon set for the primary-CTA subtext + secondary-CTA icon (kept in sync with HeroTab.js)
const SUBTEXT_ICONS = {
  CheckCircle, Shield, Star, Zap, Gift, BadgeCheck, Sparkles, Lock, Heart, ThumbsUp,
};

// Icons available for the secondary CTA button. Always includes Play
// (legacy default) plus the shared SUBTEXT_ICONS set.
const SECONDARY_CTA_ICONS = { Play, ...SUBTEXT_ICONS };

// Inject <style> into document.head so it isn't wrapped/quarantined by the
// Visual Editor (which wraps inline JSX <style> tags in <span>, making rules
// invalid and dropping their effect to 0). Returns cleanup that removes the tag.
function useHeadStyle(id, css) {
  useEffect(() => {
    if (!css) {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      return;
    }
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement('style');
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = css;
    return () => { /* keep style across re-renders; cleanup on unmount via id check */ };
  }, [id, css]);
  useEffect(() => () => {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
  }, [id]);
}

export function HeroSection({ content, theme, t, goToSignup }) {
  // Build hero title font-size CSS (only when CMS overrides exist).
  // Two independent tracks: "pre" (before highlight) and "highlight" (coloured
  // span). Each has its own Mobile / Tablet / Desktop knob. If a highlight
  // breakpoint isn't set, it inherits the matching "pre" size so admins can
  // opt-in to different sizes on just one breakpoint.
  const hasTitleOverride = !!(
    content.hero?.titleMobileFontSize ||
    content.hero?.titleTabletFontSize ||
    content.hero?.titleDesktopFontSize ||
    content.hero?.titleHighlightMobileFontSize ||
    content.hero?.titleHighlightTabletFontSize ||
    content.hero?.titleHighlightDesktopFontSize
  );
  const titleCss = hasTitleOverride ? (() => {
    const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null; };
    const preM = num(content.hero?.titleMobileFontSize) ?? 2;
    const preT = num(content.hero?.titleTabletFontSize) ?? 3;
    const preD = num(content.hero?.titleDesktopFontSize) ?? 4.5;
    const hlM = num(content.hero?.titleHighlightMobileFontSize) ?? preM;
    const hlT = num(content.hero?.titleHighlightTabletFontSize) ?? preT;
    const hlD = num(content.hero?.titleHighlightDesktopFontSize) ?? preD;
    return `
      h1.hero-title-text .hero-title-pre { font-size: ${preM}rem !important; }
      h1.hero-title-text .hero-title-highlight { font-size: ${hlM}rem !important; }
      @media (min-width: 640px) {
        h1.hero-title-text .hero-title-pre { font-size: ${preT}rem !important; }
        h1.hero-title-text .hero-title-highlight { font-size: ${hlT}rem !important; }
      }
      @media (min-width: 1024px) {
        h1.hero-title-text .hero-title-pre { font-size: ${preD}rem !important; }
        h1.hero-title-text .hero-title-highlight { font-size: ${hlD}rem !important; }
      }
    `;
  })() : null;
  useHeadStyle('hero-title-font-size', titleCss);

  // Build hero subtitle font-size CSS
  const hasSubtitleOverride = !!(content.hero?.subtitleMobileFontSize || content.hero?.subtitleTabletFontSize || content.hero?.subtitleDesktopFontSize);
  const subtitleCss = hasSubtitleOverride ? (() => {
    const mob = Number(content.hero?.subtitleMobileFontSize);
    const tab = Number(content.hero?.subtitleTabletFontSize);
    const desk = Number(content.hero?.subtitleDesktopFontSize);
    const mobSize = (Number.isFinite(mob) && mob > 0) ? mob : 1.125;
    const tabSize = (Number.isFinite(tab) && tab > 0) ? tab : 1.25;
    const deskSize = (Number.isFinite(desk) && desk > 0) ? desk : 1.25;
    return `
      p.hero-subtitle-text { font-size: ${mobSize}rem !important; }
      @media (min-width: 640px) { p.hero-subtitle-text { font-size: ${tabSize}rem !important; } }
      @media (min-width: 1024px) { p.hero-subtitle-text { font-size: ${deskSize}rem !important; } }
    `;
  })() : '';
  useHeadStyle('hero-subtitle-font-size', subtitleCss);

  // Hero fade-in + slide-up animation. When enabled via CMS, each hero
  // child (badge, title, subtitle, CTA row, trust strip, stats) animates
  // in with a staggered delay. Respects prefers-reduced-motion.
  const heroAnim = content.hero?.heroAnimateIn === true;
  const heroAnimCss = heroAnim ? `
    @keyframes heroFadeSlideUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .hero-anim-in {
      opacity: 0;
      animation: heroFadeSlideUp 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
      will-change: opacity, transform;
    }
    @media (prefers-reduced-motion: reduce) {
      .hero-anim-in { animation: none !important; opacity: 1 !important; transform: none !important; }
    }
  ` : '';
  useHeadStyle('hero-animate-in', heroAnimCss);

  // Primary CTA attention animation — fires ONCE, 10 seconds after the
  // CTA button enters the viewport. Draws the eye without pestering the
  // visitor while they're still reading the hero copy. Admin picks the
  // flavour from the CMS ('shimmer' | 'halo' | 'breathe' | 'off').
  const ctaAttention = content.hero?.ctaAttention || 'shimmer';
  // Admin-configurable delay (seconds) before the attention animation
  // fires after the CTA scrolls into view. Clamped 0-60 s.
  const rawDelay = Number(content.hero?.ctaAttentionDelay);
  const ctaAttentionDelayMs = Math.max(0, Math.min(60, Number.isFinite(rawDelay) ? rawDelay : 10)) * 1000;
  // Optional repeat interval (seconds, start-to-start). 0 = fire once.
  // Enforced minimum of 5 s to prevent overlapping keyframes.
  const rawRepeat = Number(content.hero?.ctaAttentionRepeat);
  const ctaAttentionRepeatMs = (() => {
    if (!Number.isFinite(rawRepeat) || rawRepeat <= 0) return 0;
    return Math.max(5, Math.min(300, rawRepeat)) * 1000;
  })();
  const ctaRef = useRef(null);
  const [ctaAttnActive, setCtaAttnActive] = useState(false);
  useEffect(() => {
    setCtaAttnActive(false);
    if (ctaAttention === 'off' || !ctaRef.current || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    // Longest animation ~3.6 s (breathe × 2). 4 s cleanup wipes the class
    // so React can re-apply it on the next cycle (removing → re-adding a
    // class is what restarts a CSS keyframe animation).
    const CLEANUP_MS = 4000;
    let observerFired = false;
    let cycleTimer = null;
    let cleanupTimer = null;

    const runCycle = () => {
      setCtaAttnActive(true);
      cleanupTimer = setTimeout(() => setCtaAttnActive(false), CLEANUP_MS);
      if (ctaAttentionRepeatMs > 0) {
        cycleTimer = setTimeout(runCycle, ctaAttentionRepeatMs);
      }
    };

    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !observerFired) {
        observerFired = true;
        obs.disconnect();
        cycleTimer = setTimeout(runCycle, ctaAttentionDelayMs);
      }
    }, { threshold: 0.6 });
    obs.observe(ctaRef.current);
    return () => {
      obs.disconnect();
      if (cycleTimer) clearTimeout(cycleTimer);
      if (cleanupTimer) clearTimeout(cleanupTimer);
    };
  }, [ctaAttention, ctaAttentionDelayMs, ctaAttentionRepeatMs]);
  const ctaAttnClass = ctaAttnActive
    ? `hero-cta-attention hero-cta-attention--${ctaAttention}`
    : '';

  // Sticky-content mode: when ON, the hero stays pinned at top:0 while
  // subsequent sections scroll up over it. Default OFF preserves the legacy
  // behaviour where the hero scrolls naturally with the page.
  const stickyContent = content.hero?.stickyContent === true;

  return (
    <section
      data-section="hero"
      className={`relative pt-12 sm:pt-16 ${content.hero?.stats?.some(s => s.visible !== false) ? 'pb-20' : 'pb-24'} px-4 sm:px-6 lg:px-8 overflow-hidden ${stickyContent ? 'hero-sticky' : ''}`}
      style={stickyContent ? {
        // Pin the entire hero section at the viewport top so subsequent
        // sections — already wrapped in `SectionChrome` with solid background
        // — scroll up over it. Content is vertically centred via flex so it
        // sits in the middle of the visible "stage" regardless of viewport
        // height. The section itself reserves one full viewport-height of
        // layout space, then unsticks naturally when its bottom passes the
        // top of the next section.
        position: 'sticky',
        top: 0,
        minHeight: '100vh',
        zIndex: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      } : undefined}
    >
      {/* Green radial halo overlay. Hidden entirely when the nav is set to
          transparent so the hero video reads as a clean full-bleed image
          with no green tint at the top. Solid and Frosted modes keep the
          halo (it sits behind the nav so it doesn't bleed through). */}
      {content?.branding?.navHeroStyle !== 'transparent' && (
        <div className="absolute inset-0 opacity-30" style={{ background: t.gradient }} />
      )}
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-4xl lg:max-w-6xl mx-auto">
          <PromoBanner />
          {content.hero?.badge && (
            <Badge
              className={`bg-[#007a33] text-white border-[#007a33] mb-6 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px] ${heroAnim ? 'hero-anim-in' : ''}`}
              style={heroAnim ? { animationDelay: '0.05s' } : undefined}
            >
              {content.hero.badge}
            </Badge>
          )}
          
          <h1
            style={heroAnim ? { animationDelay: '0.2s' } : undefined}
            className={`${hasTitleOverride ? 'hero-title-text' : 'text-[2rem] sm:text-5xl lg:text-7xl'} font-bold mb-6 leading-[1.1] ${t.text} ${heroAnim ? 'hero-anim-in' : ''}`}>
            <span className="sm:whitespace-nowrap hero-title-pre">{content.hero?.title}</span>{' '}
            <span className={`sm:whitespace-nowrap hero-title-highlight text-transparent bg-clip-text bg-gradient-to-r ${theme === 'dark' ? 'from-[#00994d] to-[#007a33]' : 'from-[#007a33] to-[#005c26]'}`}>
              {content.hero?.titleHighlight}
            </span>
          </h1>
          
          <p className={`${hasSubtitleOverride ? 'hero-subtitle-text' : 'text-lg sm:text-xl'} ${!(content.hero?.subtitleColorLight || content.hero?.subtitleColorDark) ? t.textSecondary : ''} mb-10 max-w-[60rem] mx-auto leading-relaxed ${heroAnim ? 'hero-anim-in' : ''}`}
             style={{
               ...((content.hero?.subtitleColorLight || content.hero?.subtitleColorDark)
                 ? { color: theme === 'dark' ? (content.hero?.subtitleColorDark || '#9ca3af') : (content.hero?.subtitleColorLight || '#4b5563') }
                 : {}),
               ...(content.hero?.subtitleFontWeight ? { fontWeight: content.hero.subtitleFontWeight } : {}),
               ...(typeof content.hero?.subtitleLetterSpacing === 'number' && content.hero.subtitleLetterSpacing !== 0
                 ? { letterSpacing: `${content.hero.subtitleLetterSpacing}em` }
                 : {}),
               ...(heroAnim ? { animationDelay: '0.35s' } : {}),
             }}
             dangerouslySetInnerHTML={{ __html: content.hero?.subtitle || '' }}
          />
          
          <div
            className={`flex flex-col items-center mb-16 ${heroAnim ? 'hero-anim-in' : ''}`}
            style={heroAnim ? { animationDelay: '0.5s' } : undefined}
          >
            <div className={`flex flex-col ${content.hero?.secondaryCtaVisible === false ? '' : 'sm:flex-row'} items-center sm:items-start justify-center gap-4`}>
              <div className="flex flex-col items-center gap-1.5">
                <Button 
                  ref={ctaRef}
                  size="lg" 
                  className={`bg-[#007a33] hover:bg-[#005c26] text-white text-lg px-6 sm:px-8 py-6 rounded-xl min-w-[236px] ${ctaAttnClass}`}
                  onClick={() => {
                    try { if (navigator.vibrate) navigator.vibrate(10); } catch (_) { /* haptics not supported */ }
                    trackButtonClick('hero_start_free_today', content.hero?.ctaText || 'Start Free Today');
                    if (content.hero?.ctaPrimaryAction === 'scroll') {
                      const targetId = content.hero?.ctaPrimaryTarget || 'pricing';
                      signalPricingCtaArrival(targetId, content.hero?.ctaPrimaryPrefillWeeks);
                      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      goToSignup();
                    }
                  }}
                  data-testid="hero-primary-cta"
                >
                  {content.hero?.ctaText || "Start Free Today"}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                {content.hero?.ctaSubtext && (() => {
                  const IconComp = SUBTEXT_ICONS[content.hero?.ctaSubtextIcon];
                  const customColor = content.hero?.ctaSubtextColor;
                  const fallbackClass = theme === 'dark' ? 'text-gray-400' : 'text-gray-700';
                  return (
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs ${customColor ? '' : fallbackClass} text-center font-medium`}
                      style={customColor ? { color: customColor } : undefined}
                      data-testid="hero-primary-cta-subtext"
                    >
                      {IconComp && <IconComp className="w-3.5 h-3.5 text-[#007a33]" data-testid="hero-primary-cta-subtext-icon" />}
                      <span>{content.hero.ctaSubtext}</span>
                    </span>
                  );
                })()}
              </div>
              {content.hero?.secondaryCtaVisible !== false && (() => {
                const SecondaryIcon = SECONDARY_CTA_ICONS[content.hero?.ctaSecondaryIcon] || Play;
                return (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className={`${t.buttonSecondary} text-lg px-8 py-6 rounded-xl min-w-[236px]`}
                    onClick={() => { trackButtonClick('hero_see_how_it_works', content.hero?.ctaSecondary || 'See How It Works'); const tgt = content.hero?.ctaSecondaryTarget || 'demo'; signalPricingCtaArrival(tgt, content.hero?.ctaSecondaryPrefillWeeks); document.getElementById(tgt)?.scrollIntoView({ behavior: 'smooth' }); }}
                    data-testid="hero-secondary-cta"
                  >
                    {content.hero?.ctaSecondaryIcon !== 'none' && <SecondaryIcon className="w-5 h-5 mr-2" data-testid="hero-secondary-cta-icon" />}
                    {content.hero?.ctaSecondary || "See How It Works"}
                  </Button>
                );
              })()}
            </div>
          </div>

          {/* Community Trust Strip — stacked vertically: stars → text → badge */}
          {(content.hero?.trust_strip?.badge_url || content.hero?.trust_strip?.text) && (
            <div
              className={`flex flex-col items-center justify-center gap-3 ${content.hero?.stats?.some(s => s.visible !== false) ? 'mb-16' : ''} ${heroAnim ? 'hero-anim-in' : ''}`}
              style={heroAnim ? { animationDelay: '0.65s' } : undefined}
              data-testid="community-trust-strip"
            >
              <div className="flex items-center gap-0.5" data-testid="trust-strip-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < (content.hero.trust_strip.star_count ?? 5) ? 'fill-[#007a33] text-[#007a33]' : 'fill-gray-400 text-gray-400'}`} />
                ))}
              </div>
              {content.hero.trust_strip.text && (() => {
                // Trust-text colour: admin override per theme takes priority
                // over the theme's default secondary-text token. Falls back
                // to the token when the field is blank.
                const trustOverride = theme === 'dark'
                  ? content.hero.trust_strip.text_color_dark
                  : content.hero.trust_strip.text_color_light;
                return (
                  <span
                    className={`text-sm font-semibold tracking-wide ${!trustOverride ? t.textSecondary : ''} text-center uppercase`}
                    style={trustOverride ? { color: trustOverride } : undefined}
                    data-testid="trust-strip-text"
                  >
                    {content.hero.trust_strip.text}
                  </span>
                );
              })()}
              {content.hero.trust_strip.badge_url && (
                <img
                  src={content.hero.trust_strip.badge_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${content.hero.trust_strip.badge_url}` : content.hero.trust_strip.badge_url}
                  alt="Trust badge"
                  className="h-16 w-auto object-contain transition-opacity duration-700 ease-out opacity-0"
                  data-testid="trust-strip-badge-img"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  onLoad={(e) => { e.currentTarget.classList.remove('opacity-0'); e.currentTarget.classList.add('opacity-100'); }}
                />
              )}
            </div>
          )}

          {/* Stats */}
          {content.hero?.stats && (
            <div
              className={`flex flex-wrap justify-center gap-8 sm:gap-16 ${heroAnim ? 'hero-anim-in' : ''}`}
              style={heroAnim ? { animationDelay: '0.8s' } : undefined}
            >
              {content.hero.stats.filter(stat => stat.visible !== false).map((stat, index) => (
                <div key={index} className="text-center">
                  <div className={`text-3xl sm:text-4xl font-bold ${t.accentText}`}>{stat.value}</div>
                  <div className={t.textMuted + " text-sm"}>{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
